// A REFLOW document model: order and structure, no coordinates.
//
// Deliberately shaped as a ProseMirror/TipTap document so the editor is a drop-in rather
// than a translation layer — node names, `content` arrays, `marks` and `attrs` are theirs.
// Pagination is the renderer's job (see documentLayout/flowRender), which is the whole point
// of the model: `DocumentTemplateJson` bakes page geometry in at authoring time, so a merged
// value that grows past the bottom margin has nowhere to go.
import { z } from 'zod';

import type { ListNumberFormat } from './listMarker';
import { flowRulesSchema } from './rules';

/** zod needs a literal tuple; `LIST_NUMBER_FORMATS` is the readonly array form of the same. */
const LIST_NUMBER_FORMATS_TUPLE = [
	'decimal',
	'decimalZero',
	'lowerLetter',
	'upperLetter',
	'lowerRoman',
	'upperRoman',
] as const;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** Character-level formatting. `textStyle` carries colour, matching @tiptap/extension-color. */
const markSchema = z.discriminatedUnion('type', [
	z.object({ type: z.literal('bold') }),
	z.object({ type: z.literal('italic') }),
	z.object({ type: z.literal('underline') }),
	z.object({ type: z.literal('textStyle'), attrs: z.object({ color: hexColor }) }),
	/**
	 * Word's highlighter. Authoring markup rather than document content — it is what an analyst
	 * marks a placeholder WITH — so the editor shows it and the renderer deliberately does not
	 * draw it. Carried on the model because dropping it at import loses the only sign of which
	 * blanks still need a field.
	 */
	z.object({ type: z.literal('highlight'), attrs: z.object({ color: hexColor }) }),
]);
export type FlowMark = z.infer<typeof markSchema>;

const textNodeSchema = z.object({
	type: z.literal('text'),
	text: z.string(),
	marks: z.array(markSchema).optional(),
});

/**
 * A bound data point, as ONE atomic inline node.
 *
 * The reason the whole model exists in this shape. A token held as text can be split by Word,
 * corrupted by autocorrect's smart quotes, or typo'd into a field nobody linked — every one of
 * those is a blank in an executed deed. As a node it cannot be half-deleted, its name is not
 * spelling, and `filter` holds its arguments verbatim where no editor can reformat them.
 */
const fieldNodeSchema = z.object({
	type: z.literal('field'),
	attrs: z.object({
		name: z.string().min(1),
		/** A Liquid filter with its arguments, e.g. `currency: "GBP"`. Absent = render as-is. */
		filter: z.string().min(1).nullable().optional(),
	}),
	marks: z.array(markSchema).optional(),
});
export type FlowFieldNode = z.infer<typeof fieldNodeSchema>;

const hardBreakSchema = z.object({ type: z.literal('hardBreak') });

const inlineSchema = z.discriminatedUnion('type', [
	textNodeSchema,
	fieldNodeSchema,
	hardBreakSchema,
]);
export type FlowInline = z.infer<typeof inlineSchema>;

/**
 * The rule that decides whether a block appears, or absent for content every recipient receives.
 *
 * An id rather than an inline condition, and a single string, because this has to ride on the
 * nodes TipTap already provides: the editor declares it as one more pass-through attribute, and
 * applying it to a selection is the same `updateAttributes` call that sets alignment. A wrapper
 * node would mean teaching it to every recursive walker over the document, three of which are
 * safety checks that fail silently.
 */
const ruleAttr = z.string().min(1).optional();

/** Paragraph-level properties the renderer consumes. Millimetres, never coordinates. */
const paragraphAttrsSchema = z.object({
	rule: ruleAttr,
	align: z.enum(['left', 'center', 'right']).optional(),
	indentLeftMm: z.number().optional(),
	indentRightMm: z.number().optional(),
	/** Positive indents the first line, negative hangs it. Both are honoured here. */
	firstLineIndentMm: z.number().optional(),
	spaceBeforeMm: z.number().optional(),
	spaceAfterMm: z.number().optional(),
	/** Multiple of the font size. */
	lineHeight: z.number().positive().optional(),
	fontSizePt: z.number().positive().optional(),
	fontFamily: z.enum(['sans-serif', 'serif', 'monospace']).optional(),
	/** Starts a new page before this block. */
	pageBreakBefore: z.boolean().optional(),
});

const paragraphSchema = z.object({
	type: z.literal('paragraph'),
	attrs: paragraphAttrsSchema.optional(),
	content: z.array(inlineSchema).optional(),
});
export type FlowParagraph = z.infer<typeof paragraphSchema>;

const imageSchema = z.object({
	type: z.literal('image'),
	attrs: z.object({
		rule: ruleAttr,
		src: z.string().min(1),
		widthMm: z.number().positive(),
		heightMm: z.number().positive(),
		pageBreakBefore: z.boolean().optional(),
	}),
});
export type FlowImage = z.infer<typeof imageSchema>;

/**
 * Where a signature stamp goes, as a node rather than an (x, y): a reflow document paginates
 * per recipient, so a template-time page number is a page out of someone else's document. The
 * attrs are the slot key the PDF path stores, plus how the author sized and positioned it.
 *
 * `widthMm` and `offsetXMm` are the only authored geometry, and both are HORIZONTAL. The anchor
 * still owns its own vertical band, which is what keeps a stamp off the text: nothing wraps
 * beside it, so no amount of moving or resizing can put it on top of a clause. Vertical position
 * is the anchor's place in the content, edited by moving the node — there is no y to author.
 */
const signatureSchema = z.object({
	type: z.literal('signature'),
	attrs: z.object({
		rule: ruleAttr,
		signingRoleId: z.string().min(1),
		isWitness: z.boolean(),
		signatureIndex: z.number().int().nonnegative(),
		/**
		 * Authored width. Absent = the design size for this anchor, which is what every
		 * document written before resizing existed means, so they render unchanged.
		 *
		 * Height is deliberately NOT stored: it follows from the ratio the stamp renderer lays
		 * out against (`anchoredStampAspectRatio`, which differs for a witness). A stored height
		 * is a second source for one fact, and the one that loses draws a clipped stamp.
		 */
		widthMm: z.number().positive().optional(),
		/**
		 * Offset from the left text edge of whatever holds the anchor — the page's content box,
		 * a list item's indent, or a table cell. Absent = 0, the flush-left position every
		 * existing anchor already renders at. Clamped at render so a stamp cannot be pushed
		 * past the right margin by a document authored against a wider page.
		 */
		offsetXMm: z.number().nonnegative().optional(),
		// No `pageBreakBefore`: nothing sets it and the editor schema would drop it on
		// round-trip. Add it to both when there is a control for it.
	}),
});
export type FlowSignature = z.infer<typeof signatureSchema>;

/**
 * Where the post-signing date goes; geometry as for `signatureSchema`. The placement id lives on
 * the node, not document order, because the date-stamp ledger references that row.
 */
const dateSchema = z.object({
	type: z.literal('date'),
	attrs: z.object({
		/**
		 * A UUID, not any string: it goes into a UUID FK column inside generation's transaction,
		 * so a malformed one would fail issuance rather than the save.
		 */
		templateDatePlacementId: z.string().uuid(),
		widthMm: z.number().positive().optional(),
		offsetXMm: z.number().nonnegative().optional(),
	}),
});
export type FlowDate = z.infer<typeof dateSchema>;

// Lists and tables nest, so their bodies are lazy. Depth is bounded by the source document.
// An item carries a rule of its own so one entry of a numbered list can be conditional. The
// list renumbers around a dropped item because markers are derived from position at render.
const listItemSchema: z.ZodType<{
	type: 'listItem';
	attrs?: { rule?: string };
	content: FlowBlock[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('listItem'),
		attrs: z.object({ rule: ruleAttr }).optional(),
		content: z.array(blockSchema),
	})
);

/**
 * Where a list's items sit, as Word states it: the text edge from the left margin, and how far
 * the marker hangs to the left of it. Absolute, not per nesting step — a clause and its 1.1 can
 * share an indent, and a sub-clause from another list can sit deeper than its depth suggests.
 * Absent = the renderer's fixed step per depth, which is what every list stored before these
 * existed renders at.
 */
const listIndentAttrs = {
	indentLeftMm: z.number().optional(),
	/** Negative = a first-line indent: the marker sits right of the text edge, not in a gutter. */
	hangingMm: z.number().optional(),
	/** Word's tab stops from the margin. The tab after the marker lands on the next one. */
	tabStopsMm: z.array(z.number()).optional(),
	/** Word's `w:suff`: a space or nothing after the marker. Absent = a tab. */
	markerSuffix: z.enum(['space', 'nothing']).optional(),
};

/**
 * Which Word counter a list advances. Lists sharing a `listId` share counters across the whole
 * document, so a list resumed after an unnumbered paragraph continues rather than restarting,
 * and still renumbers when an earlier item is removed.
 */
const listNumberingSchema = z.object({
	listId: z.string().min(1),
	/** Word's `w:ilvl`: the counter this list advances, and the `%N` its template calls `%level+1`. */
	level: z.number().int().min(0).max(8),
	/** Word's `w:startOverride`: the first item's value, where the source restarted the count. */
	restartAt: z.number().int().nonnegative().optional(),
	/**
	 * Word's `w:numId`: which numbering INSTANCE restarted. An instance restarts once, so the
	 * restart is spent on the first list carrying this id — a list the editor split or copied
	 * states the same id and carries on counting rather than restarting a second time.
	 */
	restartId: z.string().min(1).optional(),
	/** Word's `w:isLgl`: every counter in the marker is written in digits. */
	legal: z.boolean().optional(),
	/** Levels 0 to `level`, for the ancestors a template such as `%3.%4` states. */
	levels: z
		.array(
			z.object({
				start: z.number().int().nonnegative(),
				numberFormat: z.enum(LIST_NUMBER_FORMATS_TUPLE),
			})
		)
		.min(1),
});
export type FlowListNumbering = z.infer<typeof listNumberingSchema>;

const bulletListSchema: z.ZodType<{
	type: 'bulletList';
	attrs?: {
		bullet?: string;
		rule?: string;
		numbering?: FlowListNumbering;
		indentLeftMm?: number;
		hangingMm?: number;
		tabStopsMm?: number[];
		markerSuffix?: 'space' | 'nothing';
	};
	content: FlowListItem[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('bulletList'),
		attrs: z
			.object({
				rule: ruleAttr,
				/** The glyph to draw, already mapped out of Word's private-use area. */
				bullet: z.string().min(1).optional(),
				/**
				 * A bullet level counts in Word like any other, so it advances the shared
				 * counters and resets the deeper ones. Absent = not from Word.
				 */
				numbering: listNumberingSchema.optional(),
				...listIndentAttrs,
			})
			.optional(),
		content: z.array(listItemSchema),
	})
);

const orderedListSchema: z.ZodType<{
	type: 'orderedList';
	attrs?: {
		start?: number;
		numberFormat?: ListNumberFormat;
		markerTemplate?: string;
		numbering?: FlowListNumbering;
		indentLeftMm?: number;
		hangingMm?: number;
		tabStopsMm?: number[];
		markerSuffix?: 'space' | 'nothing';
		rule?: string;
	};
	content: FlowListItem[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('orderedList'),
		attrs: z
			.object({
				rule: ruleAttr,
				start: z.number().int().optional(),
				/**
				 * How to count and how to write it. Held on the LIST so the renderer derives
				 * every item's marker from its position — insert an item at the top and the
				 * rest renumber, which a per-item label could never do.
				 */
				numberFormat: z.enum(LIST_NUMBER_FORMATS_TUPLE).optional(),
				markerTemplate: z.string().optional(),
				/** Absent = numbered by position within this list alone. */
				numbering: listNumberingSchema.optional(),
				...listIndentAttrs,
			})
			.optional(),
		content: z.array(listItemSchema),
	})
);

const tableCellSchema: z.ZodType<{
	type: 'tableCell';
	attrs?: {
		colspan?: number;
		rowspan?: number;
		bg?: string;
		valign?: 'top' | 'middle' | 'bottom';
	};
	content: FlowBlock[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('tableCell'),
		attrs: z
			.object({
				colspan: z.number().int().min(1).optional(),
				rowspan: z.number().int().min(1).optional(),
				bg: hexColor.optional(),
				valign: z.enum(['top', 'middle', 'bottom']).optional(),
			})
			.optional(),
		content: z.array(blockSchema),
	})
);

// A ROW may be conditional; a cell deliberately may not. Column widths, colspan occupancy and
// vertical merges are resolved in index lockstep, so dropping one cell would shift every cell
// right of a merge into the wrong column.
const tableRowSchema: z.ZodType<{
	type: 'tableRow';
	attrs?: { isHeader?: boolean; minHeightMm?: number; rule?: string };
	content: FlowTableCell[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('tableRow'),
		attrs: z
			.object({
				rule: ruleAttr,
				isHeader: z.boolean().optional(),
				minHeightMm: z.number().nonnegative().optional(),
			})
			.optional(),
		content: z.array(tableCellSchema),
	})
);

const cellMarginsSchema = z.object({
	top: z.number().nonnegative(),
	right: z.number().nonnegative(),
	bottom: z.number().nonnegative(),
	left: z.number().nonnegative(),
});
export type FlowCellMargins = z.infer<typeof cellMarginsSchema>;

const tableSchema: z.ZodType<{
	type: 'table';
	attrs: {
		columnWidthsMm: number[];
		layout?: 'autofit' | 'fixed';
		widthMm?: number;
		align?: 'left' | 'center' | 'right';
		indentMm?: number;
		cellMarginsMm?: FlowCellMargins;
		borderWidthMm?: number;
		borderColor?: string;
		pageBreakBefore?: boolean;
		rule?: string;
	};
	content: FlowTableRow[];
}> = z.lazy(() =>
	z.object({
		type: z.literal('table'),
		attrs: z.object({
			rule: ruleAttr,
			/** The grid: each column's preferred width. */
			columnWidthsMm: z.array(z.number().positive()).min(1),
			/**
			 * Whether a column grows to fit its longest word, as Word's default table does.
			 * Absent = fixed, which is how every table stored before this was read renders.
			 */
			layout: z.enum(['autofit', 'fixed']).optional(),
			/** `w:tblW`, already resolved against its section's text width. Absent = auto. */
			widthMm: z.number().positive().optional(),
			/** Absent = left. */
			align: z.enum(['left', 'center', 'right']).optional(),
			/** `w:tblInd`: how far a left-aligned table sits in from the margin. */
			indentMm: z.number().optional(),
			/** Absent = the renderer's uniform default, as before Word's were carried. */
			cellMarginsMm: cellMarginsSchema.optional(),
			borderWidthMm: z.number().nonnegative().optional(),
			borderColor: hexColor.optional(),
			pageBreakBefore: z.boolean().optional(),
		}),
		content: z.array(tableRowSchema),
	})
);

// A signature is a block, so it is legal wherever a paragraph is — including a table cell,
// which is where a Word signature block actually lives.
const blockSchema: z.ZodType<FlowBlock> = z.lazy(() =>
	z.union([
		paragraphSchema,
		bulletListSchema,
		orderedListSchema,
		tableSchema,
		imageSchema,
		signatureSchema,
		dateSchema,
	])
);

export type FlowListItem = {
	type: 'listItem';
	attrs?: { rule?: string };
	content: FlowBlock[];
};
export type FlowBulletList = {
	type: 'bulletList';
	attrs?: {
		bullet?: string;
		rule?: string;
		numbering?: FlowListNumbering;
		indentLeftMm?: number;
		hangingMm?: number;
		tabStopsMm?: number[];
		markerSuffix?: 'space' | 'nothing';
	};
	content: FlowListItem[];
};
export type FlowOrderedList = {
	type: 'orderedList';
	attrs?: {
		start?: number;
		numberFormat?: ListNumberFormat;
		markerTemplate?: string;
		numbering?: FlowListNumbering;
		indentLeftMm?: number;
		hangingMm?: number;
		tabStopsMm?: number[];
		markerSuffix?: 'space' | 'nothing';
		rule?: string;
	};
	content: FlowListItem[];
};
export type FlowTableCell = z.infer<typeof tableCellSchema>;
export type FlowTableRow = z.infer<typeof tableRowSchema>;
export type FlowTable = z.infer<typeof tableSchema>;
export type FlowBlock =
	| FlowParagraph
	| FlowBulletList
	| FlowOrderedList
	| FlowTable
	| FlowImage
	| FlowSignature
	| FlowDate;

/** One page size for the document. Word's last section wins, as it does today. */
export const flowPageSchema = z.object({
	widthMm: z.number().positive(),
	heightMm: z.number().positive(),
	marginTopMm: z.number().nonnegative(),
	marginRightMm: z.number().nonnegative(),
	marginBottomMm: z.number().nonnegative(),
	marginLeftMm: z.number().nonnegative(),
});
export type FlowPage = z.infer<typeof flowPageSchema>;

export const GLOBAL_DOCUMENT_FLOW_VERSION = 1;

export const documentFlowSchema = z.object({
	version: z.number().int().positive(),
	type: z.literal('doc'),
	attrs: z.object({
		page: flowPageSchema,
		/** Default run size/face for a paragraph that states none. */
		baseFontSizePt: z.number().positive(),
		baseFontFamily: z.enum(['sans-serif', 'serif', 'monospace']),
		/** Word's `w:defaultTabStop`, the interval of the stops nobody set. Absent = 12.7mm. */
		defaultTabStopMm: z.number().positive().optional(),
	}),
	content: z.array(blockSchema),
	/** Field name → Liquid expression. Empty/absent = not linked, which generation refuses. */
	fieldBindings: z.record(z.string()).optional(),
	/**
	 * Rule id → the test that decides whether its content appears. Blocks reference a rule by id.
	 * Kept beside `fieldBindings` rather than inside the node tree for the same reason: it
	 * describes how the body resolves, not what the body is, and an editor that could put it in
	 * an undo step could undo a rule while leaving the content that depends on it.
	 */
	rules: flowRulesSchema.optional(),
	/** What the import could not carry over. Surfaced to the analyst, never only logged. */
	conversionWarnings: z.array(z.string()).optional(),
});

export type DocumentFlowDoc = z.infer<typeof documentFlowSchema>;

export const parseDocumentFlow = (value: unknown): DocumentFlowDoc =>
	documentFlowSchema.parse(value);
